// src/api/features/customers/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createCustomer,
  attachCustomer,
  updateCustomer,
  deleteCustomer,
  toggleCustomerWallet,
  toggleCustomerHoldBooking,
  createCustomerWalletTransaction,
} from "./api";

import type {
  CreateCustomerBody,
  AttachCustomerBody,
  UpdateCustomerBody,
  PaginatedCustomers,
  CustomerResult,
  CustomerWallet,
  ToggleWalletBody,
  CustomerHoldBooking,
  ToggleHoldBookingBody,
  CreateWalletTransactionBody,
} from "./types";

import { CustomersKeys } from "./queries";

/* =====================================
 * Local helper types (for mutation context)
 * ===================================== */

type CustomersListSnapshot = {
  key: readonly unknown[];
  data: PaginatedCustomers | undefined;
};

type UpdateCustomerContext = {
  prevDetail?: CustomerResult;
  prevLists?: CustomersListSnapshot[];
};

type DeleteCustomerContext = {
  prevLists?: CustomersListSnapshot[];
};

/* =====================================
 * Customer CRUD mutations
 * ===================================== */

/** 🔹 Create customer */
export function useCreateCustomerMutation() {
  const qc = useQueryClient();

  return useMutation<CustomerResult, unknown, CreateCustomerBody>({
    mutationFn: (payload: CreateCustomerBody) => createCustomer(payload),

    onSuccess: (created) => {
      // Option A: don't set detail at all, just invalidate
      // so that any useCustomer(created.id) will fetch from backend.
      qc.invalidateQueries({ queryKey: CustomersKeys.all });
      qc.invalidateQueries({ queryKey: CustomersKeys.detail(created.id) });

      // If you *really* want an optimistic first paint, you can keep this:
      // qc.setQueryData<CustomerResult>(CustomersKeys.detail(created.id), created);
      // but keep the invalidate for detail so it refetches:
      // qc.invalidateQueries({ queryKey: CustomersKeys.detail(created.id) });
    },
  });
}


/** 🔹 Attach a customer profile to an existing user */
export function useAttachCustomerMutation() {
  const qc = useQueryClient();

  return useMutation<CustomerResult, unknown, AttachCustomerBody>({
    mutationFn: (payload: AttachCustomerBody) => attachCustomer(payload),

    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: CustomersKeys.all });
      qc.invalidateQueries({ queryKey: CustomersKeys.detail(created.id) });
    },
  });
}

/** 🔹 Update customer */
export function useUpdateCustomerMutation() {
  const qc = useQueryClient();

  return useMutation<
    CustomerResult,
    unknown,
    { id: number | string; data: UpdateCustomerBody },
    UpdateCustomerContext
  >({
    mutationFn: ({ id, data }) => updateCustomer(id, data),

    async onMutate({ id, data }) {
      // Cancel relevant queries for this customer + all customer lists
      await Promise.all([
        qc.cancelQueries({ queryKey: CustomersKeys.detail(id) }),
        qc.cancelQueries({ queryKey: CustomersKeys.all }),
      ]);

      const prevDetail = qc.getQueryData<CustomerResult>(
        CustomersKeys.detail(id)
      );

      const prevLists: CustomersListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: CustomersKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedCustomers | undefined,
        }));

      // Optimistic detail patch
      if (prevDetail) {
        qc.setQueryData<CustomerResult>(CustomersKeys.detail(id), {
          ...prevDetail,
          ...data,
        });
      }

      // Optimistic list patch
      for (const snap of prevLists) {
        const list = snap.data;
        if (!list?.items) continue;

        const patched = list.items.map((c) =>
          c.id === Number(id)
            ? ({
                ...c,
                ...data,
              } as CustomerResult)
            : c
        );

        qc.setQueryData<PaginatedCustomers>(snap.key, {
          ...list,
          items: patched,
        });
      }

      return { prevDetail, prevLists };
    },

    onError(_err, _vars, ctx) {
      if (!ctx) return;

      if (ctx.prevDetail) {
        qc.setQueryData<CustomerResult>(
          CustomersKeys.detail(ctx.prevDetail.id),
          ctx.prevDetail
        );
      }

      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, { id }) {
      qc.setQueryData<CustomerResult>(CustomersKeys.detail(id), updated);
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: CustomersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: CustomersKeys.all });
    },
  });
}

/** 🔹 Delete customer */
export function useDeleteCustomerMutation() {
  const qc = useQueryClient();

  return useMutation<
    { message: string },
    unknown,
    number | string,
    DeleteCustomerContext
  >({
    mutationFn: (id: number | string) => deleteCustomer(id),

    async onMutate(id) {
      await qc.cancelQueries({ queryKey: CustomersKeys.all });

      const numericId = Number(id);
      const prevLists: CustomersListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: CustomersKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedCustomers | undefined,
        }));

      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;

        qc.setQueryData<PaginatedCustomers>(snap.key, {
          ...data,
          items: data.items.filter((c) => c.id !== numericId),
          total: Math.max(0, (data.total ?? 1) - 1),
        });
      }

      // Drop any cached detail record
      qc.removeQueries({ queryKey: CustomersKeys.detail(id) });

      return { prevLists };
    },

    onError(_err, _id, ctx) {
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSettled() {
      qc.invalidateQueries({ queryKey: CustomersKeys.all });
    },
  });
}

export function useToggleCustomerWalletMutation() {
  const qc = useQueryClient();

  return useMutation<
    CustomerWallet,
    unknown,
    { id: number | string; data: ToggleWalletBody }
  >({
    mutationFn: ({ id, data }) => toggleCustomerWallet(id, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: CustomersKeys.wallet(id) });
      qc.invalidateQueries({ queryKey: CustomersKeys.walletTransactions(id) });
      qc.invalidateQueries({ queryKey: CustomersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: CustomersKeys.all });
    },
  });
}

/**
 * Grant / revoke the flight hold permission for one customer.
 *
 * The flag rides on the customer detail payload, so invalidating `detail`
 * (and the list) is what makes the toggle reflect server truth rather than
 * local optimism — a rejected grant must snap back to off.
 */
export function useToggleCustomerHoldBookingMutation() {
  const qc = useQueryClient();

  return useMutation<
    CustomerHoldBooking,
    unknown,
    { id: number | string; data: ToggleHoldBookingBody }
  >({
    mutationFn: ({ id, data }) => toggleCustomerHoldBooking(id, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: CustomersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: CustomersKeys.all });
    },
  });
}

export function useCreateCustomerWalletTransactionMutation() {
  const qc = useQueryClient();

  return useMutation<
    CustomerWallet,
    unknown,
    { id: number | string; data: CreateWalletTransactionBody }
  >({
    mutationFn: ({ id, data }) => createCustomerWalletTransaction(id, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: CustomersKeys.wallet(id) });
      qc.invalidateQueries({ queryKey: CustomersKeys.walletTransactions(id) });
      qc.invalidateQueries({ queryKey: CustomersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: CustomersKeys.all });
    },
  });
}
